import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';
import { DialogComponent } from '../../../../shared/dialog/dialog.component';
import { ReferenceCache } from '../../../../app/reference/services/reference.cache';
import { PartComponent } from '../../../../app/reference/models/part-component';
import { UserCache } from '../../services/user/user.cache';
import { ComponentSelector } from '../../component-selection';
import { ComponentGroupService } from '../../services/form-service/component-group.service';
import { EstimateTotalService } from '../../services/estimate-total.service';

@Component({
  selector: 'part-estimate',
  templateUrl: './part-estimate.component.html',
  animations: [
    trigger('expandCollapse', [
      state('closed', style({ height: '0', overflow: 'hidden', opacity: 0 })),
      state('open', style({ height: '*', overflow: 'hidden', opacity: 1 })),
      transition('closed <=> open', animate('200ms ease-in-out'))
    ])]
})
export class PartEstimateComponent extends ComponentSelector implements OnChanges {
  @Input() override form: UntypedFormGroup;
  @Input({ required: true }) override componentGroupKey: number;
  @Input() override isReadOnly: boolean = false;
  @Output() remove = new EventEmitter<void>();

  public partTaxFormControl: UntypedFormControl;
  public selectedPartComponent: PartComponent;

  public taxPercentage: number = 0;
  public componentGroupName: string = '';
  private _componentLabelText: string = 'Component *';
  private _enableComponentClear: boolean = false;
  public etaOptions = [
    { label: 'In Stock', value: 0 },
    { label: '1 Day', value: 1 },
    { label: '2 Days', value: 2 },
    { label: '3 Days', value: 3 },
    { label: '4 Days', value: 4 },
    { label: '5+ Days', value: 5 }
  ];

  constructor(private userCache: UserCache,
              protected override referenceCache: ReferenceCache,
              protected override componentGroupService: ComponentGroupService,
              private dialog: MatDialog) {
    super(referenceCache, componentGroupService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.shouldMatchFilterOnGroupName = false; // Do not filter by group name.

    this.form = new UntypedFormGroup({
      'component': this.componentFormControl,
      'partNumber': new UntypedFormControl("", [Validators.required, Validators.maxLength(30)]),
      'etaDays': new UntypedFormControl("", [Validators.required, Validators.min(0), Validators.max(99)]),
      'price': new UntypedFormControl("", [Validators.required, Validators.min(0), Validators.max(99999.99)]),
      'quantity': new UntypedFormControl("", [Validators.required, Validators.min(0), Validators.max(999)]),
      'isOem': new UntypedFormControl(false),
      'description': new UntypedFormControl("", [Validators.maxLength(100)]),
    });

    // Handle read-only state changes
    this.updateReadOnlyState();

    // Handle when parent concern group changes.
    this.componentGroupService.getSubject(this.componentGroupKey)
        .pipe(takeUntil(this._destroying$))
        .subscribe(groupName => {
          // Update cached values when group name changes
          this.updateCachedValues(groupName ?? '');

          const selectedOption = this.componentFormControl.value as PartComponent;
          const componentByGroup = this.getComponent(selectedOption?.name, groupName);

          // If the new group matches the selection component group.
          if (selectedOption && selectedOption.groupName === groupName) {
            // If new group matches, disable selection and clear errors.
            this.componentFormControl.disable();
            this.componentFormControl.setErrors(null);
          }
          else if (componentByGroup) {
            // If new group contains a component with the same name as selected component,
            // update selection (swap component to new group).
            this.selectedPartComponent = componentByGroup;
            this.componentFormControl.setValue(this.selectedPartComponent);

            this.componentFormControl.disable();
            this.componentFormControl.setErrors(null);
          }
          else {
            // If parent concern group does not match currently selected group, enable selection and mark as invalid.
            this.componentFormControl.enable();
            this.componentFormControl.setErrors({ requiredSelection: true });
          }

          // If group is empty, disable selection.
          if (!groupName || groupName.length == 0) {
            this.componentFormControl.disable();
          }
        });

    this.userCache.updated
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => {
        this.taxPercentage = this.userCache?.selectedRepairFacility?.partTaxRate ?? 0;
      });
    this.taxPercentage = this.userCache?.selectedRepairFacility?.partTaxRate ?? 0;

    // Initialize cached values
    this.updateCachedValues(this.componentGroupService?.getName(this.componentGroupKey) ?? '');

    // Update cached values when form changes (for enableComponentClear)
    this.form.valueChanges
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => {
        this.updateCachedValues(this.componentGroupName);
        // Trigger global recalculation when part values change
        EstimateTotalService.triggerGlobalRecalculation();
      });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isReadOnly'] && this.form) {
      this.updateReadOnlyState();
    }
  }

	override ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

  /**
   * Update form control enabled/disabled state based on isReadOnly property
   */
  private updateReadOnlyState(): void {
    if (!this.form) return;

    const controls = ['partNumber', 'price', 'quantity', 'description'];

    if (this.isReadOnly) {
      controls.forEach(controlName => {
        this.form.get(controlName)?.disable();
      });
      // Special handling for etaDays - only disable if not already disabled by other logic
      if (this.form.get('etaDays')?.enabled) {
        this.form.get('etaDays')?.disable();
      }
    } else {
      controls.forEach(controlName => {
        this.form.get(controlName)?.enable();
      });
      // Special handling for etaDays - only enable if not disabled by component logic
      if (!this.componentFormControl?.disabled) {
        this.form.get('etaDays')?.enable();
      }
    }
  }

  /**
   * Updates cached values to prevent excessive getter calls during change detection.
   */
  private updateCachedValues(groupName: string): void {
    this.componentGroupName = groupName;

    // Update component label text
    let text = 'Component';
    if (groupName && groupName.length > 0) {
      text += ' | ' + groupName;
    }
    this._componentLabelText = text + ' *';

    // Update enable component clear
    this._enableComponentClear = !this.isReadOnly
      && this.componentFormControl?.value != null
      && groupName?.length > 0;
  }

  /**
   * Can clear component field when component is selected and group is not empty.
   */
  public get enableComponentClear(): boolean {
    return this._enableComponentClear;
  }

  public get total(): number {
    // console.count('PartEstimateComponent.total getter called');
    return (this.form.get('price')?.value ?? 0) * (this.form.get('quantity')?.value ?? 0);
  }

  public get totalTax(): number {
    // console.count('PartEstimateComponent.totalTax getter called');
    return this.total * this.taxPercentage / 100;
  }

  public get totalWithTax(): number {
    // console.count('PartEstimateComponent.totalWithTax getter called');
      return this.total + this.totalTax;
  }

  // TODO: remove the public getter here, causes rendering issues when bound to template (excessive calls)
  public get componentLabelText(): string {
    return this._componentLabelText;
  }

  public onSelectedComponent(): void {
    this.selectedPartComponent = this.componentFormControl?.value as PartComponent;

    this.componentFormControl.disable();
  }

  public removePart(): void {
    var deleteConfirmDialog = this.dialog.open(DialogComponent, {
      data: {
        title: 'Delete Part',
        bodyText: 'Are you sure you want to delete this part?',
        cancelButtonText: 'Cancel',
        confirmationButtonText: 'Yes, Delete'
      }
    });

    deleteConfirmDialog.afterClosed().subscribe(result => {
      if (result) {
        this.remove.emit();
        // Trigger global recalculation when line item is removed
        EstimateTotalService.triggerGlobalRecalculation();
      }
    });
  }

  public onClearComponent(): void {
    this.componentFormControl.setValue(null);
    this.selectedPartComponent = null;

    this.componentFormControl.enable();

    // Update cached values after clearing component
    this.updateCachedValues(this.componentGroupName);
  }

  // Expansion section.
  public isDescriptionOpen: boolean = false;

  public toggleAccordion(): void {
      this.isDescriptionOpen = !this.isDescriptionOpen;
  }

  /**
   * Helper method to get the ETA label by its numeric value
   * @param value The numeric ETA value
   * @returns The corresponding label string
   */
  public getEtaLabel(value: number): string {
    const option = this.etaOptions.find(eta => eta.value === value);
    return option ? option.label : value.toString();
  }

  /**
   * Handle price input event - trigger recalculation as user types
   */
  public onPriceInput(): void {
    EstimateTotalService.triggerGlobalRecalculation();
  }

  /**
   * Handle quantity input event - trigger recalculation as user types
   */
  public onQuantityInput(): void {
    EstimateTotalService.triggerGlobalRecalculation();
  }
}
